import React from 'react';
import PropTypes from 'prop-types';
import Select2 from '../Select2.jsx';

function humanize(name) {
  return name.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

// Stable reference so Select2 doesn't re-init on every parent render.
const SELECT2_OPTIONS = { minimumResultsForSearch: Infinity };

class ColorsTab extends React.Component {
  constructor(props) {
    super(props);
    this.typeData = props.gradientTypes.map((t) => ({ id: t, text: humanize(t) }));
  }

  setStop(index, value) {
    // Merge into the live gradient (functional update) rather than a closure-captured
    // one — a stale Select2 change could otherwise overwrite the other stop.
    this.props.patchStyle('gradient', (g) => ({
      ...g, stops: g.stops.map((s, i) => (i === index ? value : s)),
    }));
  }

  render() {
    const { style, patchStyle, gradientTypes } = this.props;
    const gradientOn = !!style.gradient;
    const fg = style.foreground_color;
    // "Match dot color" is meaningless with a gradient — the generator paints eyes
    // as a solid color, never the gradient. So while a gradient is on we hide the
    // match toggle and always show a solid eye-color picker.
    const matchEye = !gradientOn && style.eye_color == null;
    const eyeColor = style.eye_color || fg;

    return (
      <div className="qr-colors-tab">
        <div className="qr-row-head">
          <span className="qr-row-title">Dot fill</span>
          <label className="qr-inline-check">
            <input
              type="checkbox"
              data-testid="gradient-toggle"
              checked={gradientOn}
              onChange={(e) => patchStyle('gradient', e.target.checked
                ? { type: gradientTypes[0], stops: [fg, fg] }
                : null)}
            />
            {' '}
            Gradient
          </label>
        </div>

        {gradientOn ? (
          <div className="qr-gradient" data-testid="gradient-controls">
            <div className="qr-field">
              <label className="qr-label">Gradient type</label>
              <Select2
                className="form-control"
                data-testid="gradient-type"
                value={style.gradient.type}
                data={this.typeData}
                options={SELECT2_OPTIONS}
                onChange={(e) => {
                  // Functional update: merge the new type into live state so a stale
                  // Select2 change can't reset the (edited) stops. See setStop.
                  const { value } = e.target;
                  patchStyle('gradient', (g) => ({ ...g, type: value }));
                }}
              />
            </div>
            <label className="qr-label">Gradient colors</label>
            <div className="qr-color-row">
              <input
                type="color"
                className="qr-color-swatch"
                value={style.gradient.stops[0]}
                onChange={(e) => this.setStop(0, e.target.value)}
              />
              <input
                type="text"
                className="form-control"
                data-testid="gradient-stop-0"
                value={style.gradient.stops[0]}
                onChange={(e) => this.setStop(0, e.target.value)}
              />
            </div>
            <div className="qr-color-row qr-color-row--stacked">
              <input
                type="color"
                className="qr-color-swatch"
                value={style.gradient.stops[1]}
                onChange={(e) => this.setStop(1, e.target.value)}
              />
              <input
                type="text"
                className="form-control"
                data-testid="gradient-stop-1"
                value={style.gradient.stops[1]}
                onChange={(e) => this.setStop(1, e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="qr-color-row">
            <input
              type="color"
              className="qr-color-swatch"
              value={style.foreground_color}
              onChange={(e) => patchStyle('foreground_color', e.target.value)}
            />
            <input
              type="text"
              className="form-control"
              data-testid="foreground-hex"
              value={style.foreground_color}
              onChange={(e) => patchStyle('foreground_color', e.target.value)}
            />
          </div>
        )}

        <div className="qr-row-head qr-row-head--spaced">
          <span className="qr-row-title">Eye color</span>
          {!gradientOn && (
            <label className="qr-inline-check">
              <input
                type="checkbox"
                data-testid="match-eye-color"
                checked={matchEye}
                onChange={(e) => patchStyle('eye_color', e.target.checked ? null : style.foreground_color)}
              />
              {' '}
              Match dot color
            </label>
          )}
        </div>
        {(gradientOn || !matchEye) && (
          <div className="qr-color-row">
            <input
              type="color"
              className="qr-color-swatch"
              value={eyeColor}
              onChange={(e) => patchStyle('eye_color', e.target.value)}
            />
            <input
              type="text"
              className="form-control"
              data-testid="eye-hex"
              value={eyeColor}
              onChange={(e) => patchStyle('eye_color', e.target.value)}
            />
          </div>
        )}

        <div className="qr-row-head qr-row-head--spaced">
          <span className="qr-row-title">Background</span>
          <label className="qr-inline-check">
            <input
              type="checkbox"
              data-testid="transparent-toggle"
              checked={!!style.transparency}
              onChange={(e) => patchStyle('transparency', e.target.checked)}
            />
            {' '}
            Transparent
          </label>
        </div>
        {!style.transparency && (
          <div className="qr-color-row">
            <input
              type="color"
              className="qr-color-swatch"
              value={style.background_color}
              onChange={(e) => patchStyle('background_color', e.target.value)}
            />
            <input
              type="text"
              className="form-control"
              data-testid="background-hex"
              value={style.background_color}
              onChange={(e) => patchStyle('background_color', e.target.value)}
            />
          </div>
        )}
      </div>
    );
  }
}

ColorsTab.propTypes = {
  style: PropTypes.object.isRequired,
  patchStyle: PropTypes.func.isRequired,
  gradientTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default ColorsTab;
